import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { VALUE_RANGES, CONTRACT_STATUSES, formatValue } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import { getAllProjects, createProject, updateProject, searchPartners } from "../service/rpc";
import { useIsMobile } from "../hooks/useIsMobile";

const ITEMS_PER_PAGE = 6;

const EMPTY_FORM = { name: "", client: "", partner_id: "", value: "", endDate: "" };

export default function ContractsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [valueRange, setValueRange] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");

  const [contractsData, setContractsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [partners, setPartners] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        const domain = [];
        if (search.trim()) {
          // Search in name or partner name
          domain.push('|', ['name', 'ilike', search.trim()], ['partner_id', 'ilike', search.trim()]);
        }

        const [{ projects, count }, partnersData] = await Promise.all([
          getAllProjects(domain, ITEMS_PER_PAGE, offset),
          searchPartners()
        ]);

        const formatted = projects.map(p => ({
          id: p.id,
          name: p.name || "Unnamed Project",
          client: p.partner_id && p.partner_id.length > 1 ? p.partner_id[1] : "Unknown Client",
          partner_id_raw: p.partner_id,
          value: p.x_contract_value || 0,
          status: "Active",
          endDate: p.date && typeof p.date === 'string' ? p.date.split(' ')[0] : (p.date || "N/A")
        }));
        setContractsData(formatted);
        setTotalItems(count);
        setPartners(partnersData);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [search, currentPage]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setModalMode("create");
    setForm({ ...EMPTY_FORM, endDate: today });
    setEditTarget(null);
    setModalOpen(true);
  };

  const openEdit = (e, contract) => {
    e.stopPropagation();
    setModalMode("edit");
    setForm({
      name: contract.name,
      client: contract.client,
      partner_id: contract.partner_id_raw ? contract.partner_id_raw[0] : "",
      value: String(contract.value),
      endDate: contract.endDate
    });
    setEditTarget(contract);
    setModalOpen(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        name: form.name,
        partner_id: form.partner_id ? parseInt(form.partner_id) : false,
        x_contract_value: parseFloat(form.value) || 0,
        date: form.endDate || false,
      };

      if (modalMode === "create") {
        const newId = await createProject(payload);
        const selPartner = partners.find(p => p.id === parseInt(form.partner_id));
        setContractsData(prev => [{
          id: newId,
          name: form.name,
          client: selPartner ? selPartner.display_name : "Unknown Client",
          partner_id_raw: selPartner ? [selPartner.id, selPartner.display_name] : null,
          value: parseFloat(form.value) || 0,
          status: "Active",
          endDate: form.endDate || "N/A"
        }, ...prev]);
      } else {
        await updateProject(editTarget.id, payload);
        const selPartner = partners.find(p => p.id === parseInt(form.partner_id));
        setContractsData(prev => prev.map(c => c.id === editTarget.id
          ? {
            ...c,
            name: form.name,
            client: selPartner ? selPartner.display_name : c.client,
            partner_id_raw: selPartner ? [selPartner.id, selPartner.display_name] : c.partner_id_raw,
            value: parseFloat(form.value) || 0,
            endDate: form.endDate || c.endDate
          }
          : c
        ));
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Save failed", err);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pagedData = contractsData;

  return (
    <main className="main">
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "create" ? "Create Contract" : "Edit Contract"}
        onSave={handleSave}
        saveLabel={modalMode === "create" ? "Create" : "Update"}
        saving={saving}
      >
        <div className="modal-form__group">
          <label className="modal-form__label">Contract Name</label>
          <input className="modal-form__input" name="name" value={form.name} onChange={handleFieldChange} placeholder="e.g. Project Alpha" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Client (Customer)</label>
          <select className="modal-form__input" name="partner_id" value={form.partner_id} onChange={handleFieldChange}>
            <option value="">Select a customer...</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </select>
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Contract Value (₹)</label>
          <input className="modal-form__input" type="number" name="value" value={form.value} onChange={handleFieldChange} placeholder="0" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">End Date</label>
          <input className="modal-form__input" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
        </div>
      </Modal>

      <div className="page-header">
        <h1 className="page-header__title">Contracts</h1>
      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+  New</button>
        </div>
        <SearchBar
          search={search}
          setSearch={setSearch}
          valueRange={valueRange}
          setValueRange={setValueRange}
          valueRanges={VALUE_RANGES}
          placeholder="Search by name or client…"
          style={{ flex: 1, margin: 0 }}
        />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>

      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        <div className="status-pills">
          {CONTRACT_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`status-pills__pill status-pills__pill--${s.toLowerCase().replace(/\s+/g, '-')} ${statusFilter === s ? "status-pills__pill--selected" : ""}`}
            >
              {s}
            </button>
          ))}
        </div>

        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th">Project Name</th>
                <th className="table__th">Customer</th>
                <th className="table__th">Value</th>
                <th className="table__th">Status</th>
                <th className="table__th" style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={5}>No contracts match your filters.</td></tr>
              ) : pagedData.map((c) => (
                <tr key={c.id} className="table__row table__row--clickable" onClick={() => navigate(`/contracts/${c.id}`)}>
                  <td className="table__td"><span className="table__name">{c.name}</span></td>
                  <td className="table__td"><span className="table__client">{c.client}</span></td>
                  <td className="table__td"><span className="table__value">{formatValue(c.value)}</span></td>
                  <td className="table__td"><span className={`badge badge--${c.status.toLowerCase()}`}>{c.status}</span></td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No contracts match your filters.</div>
            ) : pagedData.map((c) => (
              <div key={c.id} className="kanban-card" onClick={() => navigate(`/contracts/${c.id}`)}>
                <div className="kanban-card__top">
                  <span className={`badge badge--${c.status.toLowerCase()}`}>{c.status}</span>
                  <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
                <h3 className="kanban-card__title" style={{ fontSize: '16px' }}>{c.name}</h3>
                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>{c.client}</div>
                <div className="kanban-card__bottom">
                  <div style={{ fontWeight: '700', color: '#10b981' }}>{formatValue(c.value)}</div>
                  <div className="kanban-card__due" style={{ color: '#64748b' }}>{c.endDate}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    </main>

  );
}
